// src/components/ErrorDialog.jsx
import React from 'react';
import './ErrorDialog.css';

const ErrorDialog = ({ error, onClose }) => {
  if (!error) return null;

  /**
   * Extract error details with proper type checking
   * Handles the normalized error structure from api.js
   */
  const getErrorDetails = () => {
    // Default values
    const defaults = {
      title: 'Error',
      message: 'An unexpected error occurred',
      details: null,
      statusCode: 500
    };

    // Validate error object
    if (!error || typeof error !== 'object') {
      return defaults;
    }

    // Extract status code (guaranteed to exist from normalized error)
    const statusCode = error.status || defaults.statusCode;

    // Extract title and message
    let title = defaults.title;
    let message = defaults.message;
    let details = null;

    // Handle normalized error structure from api.js
    if (error.detail && typeof error.detail === 'object') {
      // Structured error (like 402 Insufficient Credits)
      title = error.detail.error || defaults.title;
      message = error.detail.message || error.message || defaults.message;
      details = error.detail;
    } else if (error.detail && typeof error.detail === 'string') {
      // Simple string detail
      message = error.detail;
    } else if (error.message) {
      // Fallback to top-level message
      message = error.message;
    }

    return {
      title,
      message,
      details,
      statusCode
    };
  };

  const { title, message, details, statusCode } = getErrorDetails();

  /**
   * Determine error type for styling
   */
  const getErrorType = () => {
    if (statusCode === 402) return 'insufficient-credits';
    if (statusCode === 401 || statusCode === 403) return 'auth-error';
    if (statusCode === 404) return 'not-found';
    if (statusCode >= 500) return 'server-error';
    if (statusCode === 0) return 'network-error';
    return 'general-error';
  };

  const errorType = getErrorType();

  /**
   * Get emoji icon based on error type
   */
  const getErrorIcon = () => {
    switch (statusCode) {
      case 402:
        return '💳';
      case 401:
      case 403:
        return '🔒';
      case 404:
        return '🔍';
      case 0:
        return '🌐';
      case 500:
      case 502:
      case 503:
        return '🔧';
      default:
        return '⚠️';
    }
  };

  /**
   * Validate and display credit details
   */
  const hasCreditDetails = () => {
    return (
      details &&
      typeof details.credits_needed === 'number' &&
      typeof details.credits_available === 'number' &&
      typeof details.shortage === 'number'
    );
  };

  /**
   * Handle action button click
   */
  const handleActionClick = () => {
    if (statusCode === 402) {
      // Navigate to subscription/purchase page
      // You can replace this with navigate() from react-router if preferred
      window.location.href = '/subscription';
    } else {
      onClose();
    }
  };

  return (
    <div className="error-dialog-overlay" onClick={onClose}>
      <div 
        className={`error-dialog ${errorType}`} 
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        {/* Header */}
        <div className="error-dialog-header">
          <div className="error-icon" aria-hidden="true">
            {getErrorIcon()}
          </div>
          <h2 id="error-dialog-title">{title}</h2>
          <button 
            className="close-btn" 
            onClick={onClose}
            aria-label="Close dialog"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="error-dialog-body">
          <p id="error-dialog-description" className="error-message">
            {message}
          </p>
          
          {/* Credit Details - Only show if all fields are present and valid */}
          {hasCreditDetails() && (
            <div className="credit-details" role="region" aria-label="Credit information">
              <div className="credit-row">
                <span>Credits Needed:</span>
                <strong>{details.credits_needed}</strong>
              </div>
              <div className="credit-row">
                <span>Credits Available:</span>
                <strong>{details.credits_available}</strong>
              </div>
              <div className="credit-row shortage">
                <span>Shortage:</span>
                <strong className="shortage-value">{details.shortage}</strong>
              </div>
            </div>
          )}

          {/* Show status code in development mode */}
          {process.env.NODE_ENV === 'development' && (
            <div className="error-code">
              Error Code: {statusCode}
              {error.timestamp && ` | ${new Date(error.timestamp).toLocaleTimeString()}`}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="error-dialog-footer">
          {statusCode === 402 ? (
            <>
              <button 
                className="btn-secondary" 
                onClick={onClose}
                aria-label="Cancel"
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleActionClick}
                aria-label="Get more credits"
              >
                Get More Credits
              </button>
            </>
          ) : statusCode === 401 ? (
            <button 
              className="btn-primary" 
              onClick={() => window.location.href = '/signin'}
              aria-label="Sign in"
            >
              Sign In
            </button>
          ) : (
            <button 
              className="btn-primary" 
              onClick={onClose}
              aria-label="Close"
            >
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorDialog;